import _ from 'lodash';

import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {CancelToken} from '../../../helpers/asyncCancelHelpers';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {ApiApiToken, ApiTokenModel, parseApiToken} from '../../../models/api/apiTokenModel';
import {ApiSendOptions, deleteAsync, fetchAsync, patchAsync, postApiAsync} from '../../../services/http/http';
import {HttpError} from '../../../services/http/httpError';
import {ThunkAsyncAction} from '../../appThunk';
import {getCompanyUrl} from '../company/companySelectors';
import {updateDataAction} from '../dataGroupsActions';
import {getFlatApiTokens} from '../dataSelectors';

/*
 * Action enum.
 */

export enum ApiTokenActionsEnum {
  API_TOKEN_DELETED = '@@data/apiTokens/API_TOKEN_DELETED',
}

/*
 * Action Types.
 */

export interface ApiTokenDeletedAction extends ActionWithPayload<number> {
  type: ApiTokenActionsEnum.API_TOKEN_DELETED;
}

/*
 * Union Action type.
 */

export type ApiTokenAction = ApiTokenDeletedAction;

/*
 * Action creators.
 */

function apiTokenDeletedAction(apiTokenId: number): ApiTokenDeletedAction {
  return {
    type: ApiTokenActionsEnum.API_TOKEN_DELETED,
    payload: apiTokenId,
  };
}

/*
 * Action.
 */

export function fetchApiTokensAsyncAction(
  cancelToken?: CancelToken,
): ThunkAsyncAction<ReadonlyArray<ApiTokenModel>> {
  return async (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const fetchApiTokensAction = `${companyUrl}/apikeys/`;

    try {
      const response = await fetchAsync(fetchApiTokensAction, {cancelToken});
      // We want to filter out any oauth_clients here as we only want tokens.
      return _([...response.tokens])
        .filter((t) => !t.oauth_client)
        .map(parseApiToken)
        .compact()
        .value();
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      return [];
    }
  };
}

export function createApiTokenAsyncAction(
  name: string,
  scopes: ReadonlyArray<string>,
  options?: ApiSendOptions,
): ThunkAsyncAction<ApiTokenModel | undefined> {
  return async (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const apiTokenUrl = `${companyUrl}/apikeys/`;

    try {
      const token = await postApiAsync(ApiApiToken, apiTokenUrl, {name, scopes}, options);
      return token;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      return undefined;
    }
  };
}

export function updateApiTokenAsyncAction(
  tokenId: number,
  values: {name: string},
  options?: ApiSendOptions,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const apiTokenUrl = `${companyUrl}/apikeys/${tokenId}`;

    try {
      await patchAsync(apiTokenUrl, values, options);
      const updatedTokens = _([...getFlatApiTokens(getState())])
        .map((token) => {
          if (token.id === tokenId) {
            return {
              ...token,
              name: values.name,
            };
          }
          return token;
        })
        .value();

      dispatch(
        updateDataAction({
          apiTokens: updatedTokens,
        }),
      );
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function deleteApiTokenAsyncAction(tokenId: number, options?: ApiSendOptions): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const apiTokenUrl = `${companyUrl}/apikeys/${tokenId}`;

    try {
      await deleteAsync(apiTokenUrl, options);
      dispatch(apiTokenDeletedAction(tokenId));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}
